// JWT introspection helpers. Used by the widget to decide whether to
// pre-emptively ask the host for a fresh token before a send. Signature
// verification stays on the server — these helpers ONLY read the `exp`
// claim from an unverified base64 payload, never trust its contents
// for authentication.

// Seconds of slack between "the local check thinks the JWT is fine"
// and the actual server-side `exp`. Covers small clock skew + the
// round-trip duration so a token that's about to expire doesn't pass
// the local check and then fail server-side.
const STALENESS_BUFFER_SEC = 60

// jwtExp reads the `exp` claim (seconds since epoch) from a JWT
// payload. Returns null when the token is missing or malformed.
// No crypto, no library — JWT payloads are base64url-encoded JSON.
export function jwtExp(jwt: string | undefined): number | null {
  if (!jwt) return null
  const parts = jwt.split('.')
  if (parts.length !== 3) return null
  try {
    const payload = parts[1] ?? ''
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
    const json = atob(padded)
    const claims = JSON.parse(json) as { exp?: unknown }
    return typeof claims.exp === 'number' ? claims.exp : null
  } catch {
    return null
  }
}

// isJwtStale returns true when the JWT is past its `exp` (with a
// 60-second buffer for round-trip + clock skew), OR when the token
// is present but malformed (conservative: treat as stale and re-ask).
// Returns false for `undefined` so anonymous flows aren't gated.
export function isJwtStale(jwt: string | undefined): boolean {
  if (!jwt) return false
  const exp = jwtExp(jwt)
  if (exp == null) return true // malformed: re-ask
  return exp - STALENESS_BUFFER_SEC < Math.floor(Date.now() / 1000)
}
